import React, { useState, useEffect, useRef } from "react";
import DeleteModal from "../../../constants/DeleteModal";
import AxiosInstance from "../../../utils/AxiosInstance";
import { useShowAlert } from "../../../context/AlertContext";
import { CheckIcon, CheckLgIcon, CloseIcon, DeleteIcon, SaveIcon } from "../../../constants/Icons";

const SpecificationsModal = ({
  onClose,
  editData,
  attributeSuggest,
  onAttributesChange,
  jobcardId,
  ensureJobcard,
}) => {
  const [formData, setFormData] = useState({
    attributes: [],
  });
  const showAlert = useShowAlert();
  const [savingIndex, setSavingIndex] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showSaveIdx, setShowSaveIdx] = useState(null);
  const [isDeleteAttributeModalOpen, setDeleteAttributeModalOpen] =
    useState(false);
  const [deleteAttributeData, setDeleteAttributeData] = useState(null);
  const [btnsLoading, setBtnsLoading] = useState(false);
  const [allSuggestions, setAllSuggestions] = useState([]);
  const [showTitlePopup, setShowTitlePopup] = useState(false);
  const [highlightedAttributeIndex, setHighlightedAttributeIndex] =
    useState(-1);
  const suggestionsContainerRef = useRef(null);
  const [activeRowIdx, setActiveRowIdx] = useState(null);
  const inputRefs = useRef({});

  const [templateName, setTemplateName] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const getFilteredSuggestions = (inputValue) => {
    const existingKeys = formData.attributes.map((attr) =>
      (attr.key || "").trim().toLowerCase(),
    );

    return attributeSuggest
      .filter(
        (s) =>
          s.key.toLowerCase().includes(inputValue.toLowerCase()) &&
          !existingKeys.includes(s.key.toLowerCase()),
      )
      .map((s) => ({ key: s.key, source: "suggestion" }));
  };

  const fetchTemplateList = async () => {
    try {
      const res = await AxiosInstance.get("module/jobcard/template/list");

      if (res?.data?.status) {
        setTemplateList(res.data.results || []);
      }
    } catch (error) {
      console.error("Template fetch error", error);
    }
  };

  // --- CREATE NEW ATTRIBUTE ---
  const handleCreateAttribute = async (idx, attr) => {
    const key = attr?.key?.trim() || "";
    const value = attr?.value?.trim() || "";
    if (!key || !value) return [];

    setSavingIndex(idx);

    try {
      const id = jobcardId || (await ensureJobcard());
      const response = await AxiosInstance.post(`/module/jobcard/${id}`, {
        // titleId: editData.titleId,
        attributes: [{ key, value }],
      });

      const newAttributes = response.data?.results?.attributes || [];
      if (!newAttributes.length) throw new Error("Invalid API response");

      setFormData((prev) => {
        const updated = [...prev.attributes];
        updated[idx] = {
          ...newAttributes[0],
          saved: true,
          editing: false,
        };

        // DON'T add empty row here
        onAttributesChange?.(updated);
        return { ...prev, attributes: updated };
      });

      showAlert("success", "Attribute created successfully");
      return newAttributes;
    } catch (err) {
      console.error("Error creating attribute:", err);
      showAlert("danger", "Failed to create attribute");

      // ❌ remove just the failed row
      setFormData((prev) => {
        const updated = [...prev.attributes];
        updated.splice(idx, 1);
        return { ...prev, attributes: updated };
      });

      return [];
    } finally {
      setSavingIndex(null);
    }
  };

  // --- UPDATE ATTRIBUTE ---
  const handleUpdateAttribute = async (idx, attr) => {
    const key = attr?.key || "";
    const value = attr?.value || "";

    // Don't update if both key and value are completely empty
    if (key === "" && value === "") return;

    const previousRow = { ...formData.attributes[idx] };
    setSavingIndex(idx);

    try {
      const response = await AxiosInstance.patch(
        `/module/jobcard/${jobcardId}/attribute/${attr._id}`,
        { attributes: { key, value } },
      );

      // API returns updated object in response.data.results.attributes
      const updatedAttr = Array.isArray(response.data?.results?.attributes)
        ? response.data.results.attributes[0]
        : response.data?.results?.attributes;

      if (!updatedAttr) throw new Error("Invalid API response");

      setFormData((prev) => {
        const updated = [...prev.attributes];
        updated[idx] = {
          ...updated[idx],
          key: updatedAttr.key,
          value: updatedAttr.value,
          saved: true,
          editing: false,
        };

        onAttributesChange?.(
          updated.filter((a) => a.key !== "" || a.value !== ""),
        );

        return { ...prev, attributes: updated };
      });

      showAlert("success", "Attribute updated successfully");
    } catch (err) {
      console.error("Error updating attribute:", err);
      showAlert("danger", "Failed to update attribute");

      setFormData((prev) => {
        const updated = [...prev.attributes];
        updated[idx] = previousRow;
        return { ...prev, attributes: updated };
      });
    } finally {
      setSavingIndex(null);
    }
  };

  // --- DELETE ATTRIBUTE ---
  const handleDeleteAttribute = async () => {
    if (!deleteAttributeData?._id || !jobcardId) return;

    setBtnsLoading(true);
    const deletedAttr = deleteAttributeData;

    try {
      const response = await AxiosInstance.delete(
        `module/jobcard/${jobcardId}/attributes/${deletedAttr._id}`,
      );

      if (response?.data?.status) {
        setFormData((prev) => {
          // Remove the deleted attribute
          const updated = prev.attributes.filter(
            (attr) => attr._id !== deletedAttr._id,
          );

          // ✅ Call onAttributesChange with all non-completely-empty attributes
          onAttributesChange?.(
            updated.filter((a) => a.key !== "" || a.value !== ""),
          );

          return { ...prev, attributes: updated };
        });

        showAlert("success", "Attribute deleted successfully");
      } else {
        throw new Error("Delete failed");
      }
    } catch (error) {
      console.error("Error deleting attribute:", error);
      showAlert("danger", "Failed to delete attribute");
    } finally {
      setBtnsLoading(false);
      setDeleteAttributeModalOpen(false);
      setDeleteAttributeData(null);
    }
  };

  // const handleSaveTemplate = async () => {
  //   const attributes = formData.attributes
  //     .filter((a) => a.key?.trim() && a.value?.trim())
  //     .map((a) => ({
  //       key: a.key.trim(),
  //       value: a.value.trim(),
  //     }));

  //   if (!templateName.trim()) {
  //     showAlert("warning", "Enter template name");
  //     return;
  //   }

  //   if (attributes.length === 0) {
  //     showAlert("warning", "Add at least one attribute");
  //     return;
  //   }

  //   try {
  //     setTemplateLoading(true);

  //     let finalAttributes = [...attributes];

  //     // ✅ If template already exists, fetch existing template attributes
  //     if (selectedTemplate) {
  //       const templateRes = await AxiosInstance.get(
  //         `module/jobcard/template/${selectedTemplate}`,
  //       );

  //       const existingAttributes = templateRes?.data?.results?.attributes || [];

  //       // merge old + new
  //       finalAttributes = [
  //         ...existingAttributes.map((a) => ({
  //           key: a.key,
  //           value: a.value,
  //         })),
  //         ...attributes,
  //       ];

  //       // remove duplicates by key
  //       const uniqueMap = new Map();
  //       finalAttributes.forEach((attr) => {
  //         uniqueMap.set(attr.key.toLowerCase(), attr);
  //       });

  //       finalAttributes = Array.from(uniqueMap.values());

  //       // delete old template
  //       await AxiosInstance.delete(
  //         `module/jobcard/template/${selectedTemplate}`,
  //       );
  //     }

  //     // ✅ Create new template with merged attributes
  //     await AxiosInstance.post("module/jobcard/template/add", {
  //       templateName: templateName.trim(),
  //       attributes: finalAttributes,
  //     });

  //     showAlert("success", "Template saved successfully");

  //     // refreshTemplates?.();
  //     fetchTemplateList();

  //     setTemplateName("");
  //     setShowTemplateModal(false);
  //     onClose();
  //   } catch (error) {
  //     console.error(error);
  //     showAlert("danger", "Failed to save template");
  //   } finally {
  //     setTemplateLoading(false);
  //   }
  // };

  const handleSaveTemplate = async () => {
    const attributes = formData.attributes
      .filter((a) => a.key?.trim() && a.value?.trim())
      .map((a) => ({
        key: a.key.trim(),
        value: a.value.trim(),
      }));

    if (!templateName.trim()) {
      showAlert("warning", "Enter template name");
      return;
    }

    if (attributes.length === 0) {
      showAlert("warning", "Add at least one attribute");
      return;
    }

    try {
      setTemplateLoading(true);

      // ✅ If template is selected, delete that template first
      if (selectedTemplate) {
        await AxiosInstance.delete(
          `module/jobcard/template/${selectedTemplate}`,
        );
      }

      // ✅ Create new template
      const id = jobcardId || (await ensureJobcard());
      await AxiosInstance.post(`module/jobcard/template/add/${id}`, {
        templateName: templateName.trim(),
        // attributes: attributes,
      });

      showAlert("success", "Template saved successfully");

      fetchTemplateList();

      setTemplateName("");
      setSelectedTemplate(""); // reset selection
      setShowTemplateModal(false);
    } catch (error) {
      console.error(error);
      showAlert("danger", "Failed to save template");
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleEditClick = (idx) => {
    setFormData((prev) => ({
      ...prev,
      attributes: prev.attributes.map((a, i) => ({
        ...a,
        editing: i === idx,
      })),
    }));
  };

  const handleTemplateAttach = async (templateId) => {
    if (!templateId) return;
    const id = jobcardId || (await ensureJobcard());

    try {
      const res = await AxiosInstance.patch(
        `module/jobcard/${id}/template/${templateId}`,
      );

      const templateAttributes = res?.data?.results?.attributes || [];

      const mapped = templateAttributes.map((a) => ({
        _id: a._id,
        key: a.key || "",
        value: a.value || "",
        saved: true,
        editing: false,
      }));

      setFormData((prev) => ({
        ...prev,
        attributes: [
          ...mapped,
          {
            key: "",
            value: "",
            saved: false,
            editing: true,
          },
        ],
      }));

      // ✅ IMPORTANT: update parent state
      onAttributesChange?.(mapped);

      showAlert("success", "Template loaded successfully");
    } catch (error) {
      console.error(error);
      showAlert("danger", "Failed to load template");
    }
  };

  const selectSuggestion = (suggestion, idx) => {
    setFormData((prev) => {
      const updated = [...prev.attributes];
      updated[idx] = { ...updated[idx], key: suggestion.key };
      return { ...prev, attributes: updated };
    });
    setShowTitlePopup(false);
    setActiveRowIdx(null);
  };

  const handleTitleKeyDown = (e, idx) => {
    if (showTitlePopup && allSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedAttributeIndex((prev) =>
          prev < allSuggestions.length - 1 ? prev + 1 : 0,
        );
        return;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedAttributeIndex((prev) =>
          prev > 0 ? prev - 1 : allSuggestions.length - 1,
        );
        return;
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedAttributeIndex >= 0) {
          selectSuggestion(allSuggestions[highlightedAttributeIndex], idx);
        } else {
          const descInput = document.getElementById(`desc-${idx}`);
          if (descInput) descInput.focus();
        }
        setShowTitlePopup(false);
        return;
      } else if (e.key === "Escape") {
        setShowTitlePopup(false);
        return;
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      const descInput = document.getElementById(`desc-${idx}`);
      if (descInput) descInput.focus();
    }
  };

  // ✅ Populate attributes on open
  // useEffect(() => {
  //   if (jobcardId) {
  //     const existing =
  //       editData.attributes?.map((a) => ({
  //         key: a.key || "",
  //         value: a.value || "",
  //         _id: a._id || null,
  //         saved: true,
  //         editing: false,
  //       })) || [];

  //     existing.push({
  //       key: "",
  //       value: "",
  //       saved: false,
  //       editing: true,
  //     });

  //     setFormData({ attributes: existing });
  //   }
  // }, [editData]);

  useEffect(() => {
    const existing =
      editData?.attributes?.map((a) => ({
        key: a.key || "",
        value: a.value || "",
        _id: a._id || null,
        saved: true,
        editing: false,
      })) || [];

    // always add empty row
    existing.push({
      key: "",
      value: "",
      saved: false,
      editing: true,
    });

    setFormData({ attributes: existing });
  }, [editData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsContainerRef.current &&
        !suggestionsContainerRef.current.contains(e.target)
      ) {
        setShowTitlePopup(false);
        setActiveRowIdx(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchTemplateList();
  }, []);

  useEffect(() => {
    if (editData?.templateId) {
      setSelectedTemplate(editData.templateId);
    } else {
      setSelectedTemplate(""); // reset if no template
    }
  }, [editData?.templateId]);

  return (
    <>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show"
        tabIndex="-1"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Specifications</h5>
              <div className="d-flex align-items-center gap-4 ms-3">
                {/* <label className="form-label mb-0 fw-semibold">Template</label> */}

                <select
                  className="form-select form-select-sm"
                  style={{ width: "180px" }}
                  value={selectedTemplate || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedTemplate(value);
                    handleTemplateAttach(value);
                  }}
                >
                  <option value="">Select Template</option>

                  {templateList?.length > 0 ? (
                    templateList.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No Templates</option>
                  )}
                </select>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              {/* <div className="mb-3">
                <label className="form-label">Template Name</label>
                <div className="col-lg-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter template name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                  />
                </div>
              </div> */}
              <div className="table-responsive shadow-sm border rounded">
                <table className="table table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "45%" }}>Title</th>
                      <th style={{ width: "50%" }}>Description</th>
                      <th style={{ width: "5%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.attributes.map((attr, idx) => (
                      <tr
                        key={idx}
                        onMouseEnter={() => setHoveredRow(idx)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        {/* --- Title --- */}
                        <td className="position-relative">
                          <div className="position-relative">
                            <input
                              id={`key-${idx}`}
                              type="text"
                              className="form-control pe-2"
                              value={attr.key}
                              ref={(el) => (inputRefs.current[idx] = el)}
                              onChange={(e) => {
                                const value = e.target.value;
                                setFormData((prev) => {
                                  const updated = [...prev.attributes];
                                  updated[idx] = {
                                    ...updated[idx],
                                    key: value,
                                  };
                                  return {
                                    ...prev,
                                    attributes: updated,
                                  };
                                });

                                if (value.trim().length > 0) {
                                  setAllSuggestions(
                                    getFilteredSuggestions(value),
                                  );
                                  setShowTitlePopup(true);
                                  setHighlightedAttributeIndex(-1);
                                  setActiveRowIdx(idx);
                                } else {
                                  setAllSuggestions([]);
                                  setShowTitlePopup(false);
                                  setActiveRowIdx(null);
                                }
                              }}
                              onFocus={() => {
                                const suggestions = getFilteredSuggestions(
                                  attr.key || "",
                                );
                                if (suggestions.length > 0) {
                                  setAllSuggestions(suggestions);
                                  setShowTitlePopup(true);
                                  setHighlightedAttributeIndex(-1);
                                  setActiveRowIdx(idx);
                                }
                              }}
                              onKeyDown={(e) => handleTitleKeyDown(e, idx)}
                              placeholder="Enter Title"
                              autoComplete="off"
                            />
                          </div>
                        </td>

                        {/* --- Description --- */}
                        <td className="position-relative">
                          <div className="position-relative">
                            <input
                              id={`desc-${idx}`}
                              type="text"
                              className="form-control"
                              value={attr.value}
                              // readOnly={
                              //   attr.saved && !attr.editing
                              // }
                              style={{
                                cursor:
                                  attr.saved && !attr.editing
                                    ? "pointer"
                                    : "text",
                              }}
                              onClick={() => {
                                if (attr.saved) handleEditClick(idx);
                              }}
                              onFocus={() => {
                                if (attr.saved) handleEditClick(idx);
                              }}
                              onChange={(e) => {
                                setFormData((prev) => {
                                  const updated = [...prev.attributes];
                                  updated[idx] = {
                                    ...updated[idx],
                                    value: e.target.value,
                                  };
                                  return {
                                    ...prev,
                                    attributes: updated,
                                  };
                                });
                                setShowSaveIdx(idx); // show save button when typing
                              }}
                              placeholder="Enter Description"
                              onKeyDown={async (e) => {
                                if (e.key !== "Enter") return;

                                e.preventDefault();

                                const currentAttr = formData.attributes[idx];
                                const key = currentAttr.key?.trim() || "";
                                const value = currentAttr.value?.trim() || "";

                                // Move focus back to title if empty
                                if (!key) {
                                  const keyInput = document.getElementById(
                                    `key-${idx}`,
                                  );
                                  if (keyInput) keyInput.focus();
                                  return;
                                }

                                // Stay on description if empty
                                if (!value) return;

                                // Save attribute
                                if (currentAttr._id) {
                                  // UPDATE existing
                                  await handleUpdateAttribute(idx, currentAttr);

                                  // ✅ DO NOT append new row
                                } else {
                                  // CREATE new
                                  await handleCreateAttribute(idx, currentAttr);

                                  // Append new empty row only after create
                                  setFormData((prev) => {
                                    const updated = [...prev.attributes];

                                    // Avoid duplicate empty row
                                    const last = updated[updated.length - 1];
                                    if (!last.key && !last.value)
                                      return { ...prev, attributes: updated };

                                    updated.push({
                                      key: "",
                                      value: "",
                                      saved: false,
                                      editing: true,
                                    });
                                    return { ...prev, attributes: updated };
                                  });
                                }

                                // Clear save indicator
                                setShowSaveIdx(null);

                                // Focus the next row or description
                                setTimeout(() => {
                                  let targetIndex = idx + 1;
                                  const nextKeyEl = document.getElementById(
                                    `key-${targetIndex}`,
                                  );
                                  const nextDescEl = document.getElementById(
                                    `desc-${targetIndex}`,
                                  );

                                  if (
                                    nextKeyEl &&
                                    !formData.attributes[
                                      targetIndex
                                    ]?.key?.trim()
                                  ) {
                                    nextKeyEl.focus();
                                  } else if (nextDescEl) {
                                    nextDescEl.focus();
                                  }
                                }, 50);
                              }}
                              autoComplete="off"
                            />

                            {/* --- Save Button --- */}
                            {savingIndex === idx ? (
                              <button
                                className="btn btn-sm btn-label-secondary position-absolute top-50 end-0 translate-middle-y me-0 shadow-none"
                                disabled
                              >
                                <span className="spinner-border spinner-border-sm"></span>
                              </button>
                            ) : (
                              showSaveIdx === idx && (
                                <button
                                  className="btn btn-sm btn-label-success position-absolute top-50 end-0 translate-middle-y me-1 shadow-none"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    // always read latest attr from formData
                                    const currentAttr =
                                      formData.attributes[idx];
                                    const key = currentAttr.key?.trim() || "";
                                    const value =
                                      currentAttr.value?.trim() || "";

                                    if (!key || !value) return;

                                    if (currentAttr._id) {
                                      // update existing
                                      await handleUpdateAttribute(
                                        idx,
                                        currentAttr,
                                      );
                                    } else {
                                      // create new
                                      await handleCreateAttribute(
                                        idx,
                                        currentAttr,
                                      );
                                    }

                                    // clear save button
                                    setShowSaveIdx(null);

                                    // Add new empty row if last row was just saved
                                    if (!currentAttr._id) {
                                      setFormData((prev) => {
                                        const newAttrs = [...prev.attributes];
                                        // Add empty row only if last row is not empty
                                        const last =
                                          newAttrs[newAttrs.length - 1];
                                        if (!last.key && !last.value)
                                          return {
                                            ...prev,
                                            attributes: newAttrs,
                                          };

                                        return {
                                          ...prev,
                                          attributes: [
                                            ...newAttrs,
                                            {
                                              key: "",
                                              value: "",
                                              saved: false,
                                              editing: true,
                                            },
                                          ],
                                        };
                                      });

                                      // Focus the new row input
                                      setTimeout(() => {
                                        const newIndex =
                                          formData.attributes.length;
                                        const nextInput =
                                          document.getElementById(
                                            `key-${newIndex}`,
                                          );
                                        if (nextInput) nextInput.focus();
                                      }, 100);
                                    }
                                  }}
                                >
                                  <CheckLgIcon iClass={"fs-6"}/>
                                </button>
                              )
                            )}
                          </div>
                        </td>

                        {/* --- Delete Icon (visible on hover) --- */}
                        <td className="text-center align-middle">
                          {hoveredRow === idx &&
                            attr._id && // only for saved attributes
                            attr.key?.trim().length > 0 && ( // show if key exists, even if value empty
                              <div className="order-actions">
                                <button
                                  type="button"
                                  className="btn btn-label-danger shadow-none"
                                  onClick={() => {
                                    setDeleteAttributeData(attr);
                                    setDeleteAttributeModalOpen(true);
                                  }}
                                >
                                  <DeleteIcon/>
                                </button>
                              </div>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {showTitlePopup &&
                allSuggestions.length > 0 &&
                activeRowIdx !== null &&
                (() => {
                  const inputEl = inputRefs.current[activeRowIdx];
                  if (!inputEl) return null;
                  const rect = inputEl.getBoundingClientRect();
                  return (
                    <ul
                      ref={suggestionsContainerRef}
                      className="suggestions-list position-fixed"
                      style={{
                        top: rect.bottom + 2,
                        left: rect.left,
                        width: rect.width,
                        maxHeight: "150px",
                        overflowY: "auto",
                        background: "white",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        zIndex: 1000,
                      }}
                    >
                      {allSuggestions.map((suggestion, index) => (
                        <li
                          key={index}
                          className={`suggestion-item ${
                            highlightedAttributeIndex === index
                              ? "highlighted"
                              : ""
                          }`}
                          onMouseDown={() =>
                            selectSuggestion(suggestion, activeRowIdx)
                          }
                        >
                          {suggestion.key}
                        </li>
                      ))}
                    </ul>
                  );
                })()}
            </div>
            <div className="modal-footer d-block">
              <button
                type="button ms-4"
                className="btn btn-outline-secondary text-left"
                onClick={onClose}
              >
                <CloseIcon iClass={"fs-15"}/> Close
              </button>

              <button
                className="btn btn-primary text-right"
                // onClick={() => setShowTemplateModal(true)}
                onClick={onClose}
              >
                <CheckIcon iClass={"me-1"}/>
                Done
              </button>

              <button
                type="button"
                className="btn  btn-secondary text-right"
                onClick={() => setShowTemplateModal(true)}
                disabled={
                  formData.attributes.filter((a) => a.key && a.value).length ===
                  0
                }
              >
                <SaveIcon/>
              </button>

              {/* <button
                className="btn btn-primary text-right"
                onClick={() => setShowTemplateModal(true)}
              >
                <i className="bx bx-save me-1"></i>
                Save Template
              </button> */}
            </div>
            {isDeleteAttributeModalOpen && (
              <DeleteModal
                onClose={() => {
                  setDeleteAttributeModalOpen(false);
                  setDeleteAttributeData(null);
                }}
                onConfirm={handleDeleteAttribute}
                isLable={"Attribute " + (deleteAttributeData?.key || "")}
                isBtnLoading={btnsLoading}
              />
            )}
          </div>
        </div>
      </div>

      {showTemplateModal && (
        <>
          <div className="modal-backdrop show"></div>

          <div
            className="modal show"
            tabIndex="-1"
            role="dialog"
            style={{ display: "block" }}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Save Template</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowTemplateModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <label className="form-label">Template Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter template name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                  />
                </div>

                <div className="modal-footer d-block">
                  <button
                    className="btn btn-outline-secondary btn-sm text-left"
                    onClick={() => setShowTemplateModal(false)}
                  >
                    <CloseIcon iClass={"fs-15"}/> Cancel
                  </button>

                  <button
                    className="btn btn-sm btn-primary text-right"
                    onClick={handleSaveTemplate}
                    disabled={templateLoading}
                  >
                    {templateLoading ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <>
                        <SaveIcon iClass={"me-1"}/>
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SpecificationsModal;
